import { useCallback } from 'react'
import { addDays, endOfDay, startOfDay } from 'date-fns'
import { useFilteredTasks } from './useFilteredTasks'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'

/** Next 7 days runs from tomorrow to the end of the seventh day (design 34a). */
export function getUpcomingWindow(now: Date = new Date()) {
  return {
    today: startOfDay(now).getTime(),
    start: startOfDay(addDays(now, 1)).getTime(),
    end: endOfDay(addDays(now, 7)).getTime(),
  }
}

/**
 * Tasks for Next 7 days (design 34a): due in the window, overdue (listed on
 * top), and ranges that start in the window but end after it, so work under
 * way this week doesn't vanish. Open, not archived, assigned to me.
 */
export function useUpcomingTasks(options?: { includeSubTasks?: boolean }) {
  // Display → Show sub-tasks flattens them into the list (design 28b).
  const includeSubTasks = options?.includeSubTasks ?? false
  const predicate = useCallback(
    (task: TaskDocument, openStatusIds: ReadonlyArray<string>) => {
      if (!task.dueDate || task.isArchived) return false
      if (!openStatusIds.includes(task.statusId)) return false
      if (!includeSubTasks && task.parentId) return false
      const { today, start, end } = getUpcomingWindow()
      if (task.dueDate < today) return true
      if (task.dueDate >= start && task.dueDate <= end) return true
      return !!task.startDate && task.startDate <= end && task.dueDate > end
    },
    [includeSubTasks],
  )

  return useFilteredTasks({ predicate, assignedToMeOnly: true })
}
